import { type ReactElement, useState } from "react";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { BaseImage } from "../../../components/Content/BaseImage.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import type { StyleModelImagesKind } from "../../types.ts";
import { ModelCreationLayout } from "../components/ModelCreationLayout.tsx";
import { useModelUuid } from "../hooks/useModelUuid.ts";

export const StyleImagesKindForm = ({
  onClickNext,
  onClickPrevious,
}: {
  onClickNext: () => void;
  onClickPrevious: () => void;
}) => {
  const modelUuid = useModelUuid();
  const { mutate: updateModel, isLoading: isUpdateModelLoading } =
    useAppMutation({
      path: "references/update",
      invalidate: [`references/${modelUuid}`],
    }).mutation;
  const [loadingButton, setLoadingButton] =
    useState<StyleModelImagesKind | null>(null);

  return (
    <ModelCreationLayout
      showModelPreview
      title="What best describes your style"
      onBack={() => {
        updateModel(
          { uuid: modelUuid, images_kind: null },
          { onSuccess: onClickPrevious },
        );
      }}
    >
      <div className="flex-col-center gap-1000">
        <div className="flex-row w-full justify-center flex-wrap gap-600">
          {Object.keys(STYLE_MODEL_BUTTONS).map((it) => (
            <BaseButton
              key={it}
              loading={loadingButton === it && isUpdateModelLoading}
              onClick={() => {
                setLoadingButton(it);
                updateModel(
                  {
                    uuid: modelUuid,
                    images_kind: it,
                  },
                  {
                    onSuccess: () => {
                      setLoadingButton(null);
                      onClickNext();
                    },
                    onError: () => {
                      setLoadingButton(null);
                    },
                  },
                );
              }}
              className="max-w-[192px] max-h-[184px] w-full p-400 rounded-300 border hover:border-primary-hover"
            >
              <div className="flex-col gap-800">
                {STYLE_MODEL_BUTTONS[it].header}
                <div className="flex-col gap-100">
                  <div className="flex-row items-center justify-between">
                    <span className="body-lg-semibold text-primary ">
                      {STYLE_MODEL_BUTTONS[it].title}
                    </span>
                    <Icon name="ChevronRight" size="xs" />
                  </div>
                  <span className="body-md-default text-secondary">
                    {STYLE_MODEL_BUTTONS[it].content}
                  </span>
                </div>
              </div>
            </BaseButton>
          ))}
        </div>
      </div>
    </ModelCreationLayout>
  );
};

const STYLE_MODEL_BUTTONS: Record<
  StyleModelImagesKind,
  { header: ReactElement; title: string; content: string }
> = {
  PHOTOGRAPHY: {
    header: (
      <div className="flex-row h-1000 gap-100">
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/photography_1.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/photography_2.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/photography_3.png"
        />
      </div>
    ),
    title: "Photography",
    content: "Realistic pictures from the real world",
  },
  ILLUSTRATION_2D: {
    header: (
      <div className="flex-row h-1000 gap-100">
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/illustration_1.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/illustration_2.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/illustration_3.png"
        />
      </div>
    ),
    title: "2D Illustration",
    content: "Artworks representing objects, or concepts",
  },
  GRAPHIC_3D: {
    header: (
      <div className="flex-row h-1000 gap-100">
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/3D_1.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/3D_2.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/3D_3.png"
        />
      </div>
    ),
    title: "3D Graphic",
    content: "Computer-generated images",
  },
  ICON: {
    header: (
      <div className="flex-row h-1000 gap-100">
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/icon_1.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/icon_2.png"
        />
        <BaseImage
          className="w-1000 h-full rounded-050"
          src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/onboarding/images_kind/icon_3.png"
        />
      </div>
    ),
    title: "Icon",
    content: "A symbol of a brand or a pictogram",
  },
  OTHER: {
    header: (
      <Icon
        name="Ellipsis"
        className="h-1000 w-1000 px-100 rounded-050 border fill-primary-rest"
      />
    ),
    title: "Other",
    content: "If nothing fits in the previous categories",
  },
};
